import { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Plus, ShieldCheck, Wallet as WalletIcon, X } from "lucide-react";
import { useRazorpay } from "react-razorpay";
import api, { inr, fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const PRESETS = [500, 1000, 2500, 5000, 10000];

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [adding, setAdding] = useState(false);
  const { Razorpay } = useRazorpay();

  const load = useCallback(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    api.get("/wallet/transactions").then(({ data }) => setTxns(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addFunds = async () => {
    if (Number(amount) < 500) {
      toast.error("Minimum wallet top-up is ₹500");
      return;
    }
    setAdding(true);
    try {
      const { data: order } = await api.post("/wallet/razorpay/order", { amount: Number(amount) });
      const rzp = new Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "SHIPANY",
        description: "Wallet Top-up",
        theme: { color: "#0757D9" },
        handler: async (res) => {
          try {
            await api.post("/wallet/razorpay/verify", res);
            toast.success(`${inr(amount)} added to wallet`);
            setOpen(false);
            load();
          } catch (e) {
            toast.error(fmtErr(e));
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled — no amount was deducted") },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed or was cancelled. No amount was deducted."));
      rzp.open();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="wallet-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Wallet</h1>
      <p className="text-sm text-slate-500">Prepaid balance powers every booking.</p>

      <div className="mt-6 grid gap-5 sm:grid-cols-3">
        <div className="rounded-2xl bg-shipany-navy p-6 text-white sm:col-span-2" data-testid="wallet-balance-card">
          <WalletIcon className="h-6 w-6 text-shipany-cyan" />
          <p className="mt-4 text-xs uppercase tracking-widest text-slate-400">Available Balance</p>
          <p className="mono mt-1 text-4xl font-bold" data-testid="wallet-balance">{inr(wallet?.balance ?? 0)}</p>
          <button onClick={() => setOpen(true)} data-testid="wallet-add-funds-btn"
            className="mt-5 flex items-center gap-2 rounded-xl bg-shipany-cyan px-5 py-2.5 text-sm font-bold text-shipany-navy transition-colors hover:bg-[#0EB4DE] glow-cyan">
            <Plus className="h-4 w-4" /> Add Funds
          </button>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">This month</p>
          <p className="mono mt-2 text-xl font-bold text-emerald-600">+{inr(txns.filter((t) => t.type === "credit").reduce((a, t) => a + t.amount, 0))}</p>
          <p className="text-xs text-slate-400">credits</p>
          <p className="mono mt-3 text-xl font-bold text-red-500">−{inr(txns.filter((t) => t.type === "debit").reduce((a, t) => a + t.amount, 0))}</p>
          <p className="text-xs text-slate-400">debits</p>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="wallet-transactions">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Transaction</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Balance</th><th className="px-4 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.txn_id} className="border-t border-slate-100">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full ${t.type === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                      {t.type === "credit" ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                    </span>
                    <div>
                      <p className="font-medium text-shipany-navy">{t.note}</p>
                      <p className="mono text-[10px] text-slate-400">{t.ref}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3.5 capitalize text-slate-500">{t.category}</td>
                <td className={`mono px-4 py-3.5 font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>{t.type === "credit" ? "+" : "−"}{inr(t.amount)}</td>
                <td className="mono px-4 py-3.5 text-slate-500">{inr(t.balance_after)}</td>
                <td className="px-4 py-3.5 text-slate-500">{fmtDateTime(t.created_at)}</td>
              </tr>
            ))}
            {txns.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-funds-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Add Funds</h2>
              <button onClick={() => setOpen(false)} data-testid="add-funds-close-btn" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => setAmount(p)} data-testid={`preset-${p}`}
                  className={`rounded-lg px-4 py-2 text-sm font-bold ${amount === p ? "bg-shipany-blue text-white" : "bg-slate-100 text-slate-600"}`}>{inr(p)}</button>
              ))}
            </div>
            <input data-testid="add-funds-amount" type="number" min={500} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mono mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-lg font-bold focus:border-shipany-blue focus:outline-none" />
            <button onClick={addFunds} disabled={adding} data-testid="add-funds-confirm-btn"
              className="mt-4 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
              {adding ? "Processing…" : `Pay ${inr(amount)}`}
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400" data-testid="razorpay-secure-note">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Secured by Razorpay · Minimum top-up ₹500 · UPI, cards, netbanking
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
